import { observer } from "mobx-react-lite";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { RefreshCw, Settings, FileText, XCircle } from "lucide-react";
import { useEffect, useState, useMemo } from "react";
import { useCarbonDashboardStore } from "../hooks";
import { useAuthStore } from "@/features/auth";
import { Can } from "@/hooks/useCan/Can";
import { PATHS } from "@/lib/router";
import { useNavigate, useParams } from "react-router-dom";
import CarbonDashboardFilters from "../components/dashboard/CarbonDashboardFilters";
import { useOrganizationStore } from "@/features/organization";
import { generateYearOptions } from "../utils/year-helpers";
import { calculateCarbonPerformance } from "../utils/carbon-performance";
import CarbonOverviewCards from "../components/dashboard/CarbonOverviewCards";
import CarbonTrendAnalysis from "../components/dashboard/CarbonTrendAnalysis";
import CarbonPerformanceCharts from "../components/dashboard/CarbonPerformanceCharts";
import CarbonActivityBreakdown from "../components/dashboard/CarbonActivityBreakdown";
import CarbonSMEContribution from "../components/dashboard/CarbonSMEContribution";
import { useTran } from "@/hooks";

const CarbonInventoryDashboardPage = observer(() => {
  const {
    loadCarbonTargets,
    carbonTargets,
    carbonSubmissionsData,
    carbonOverviewData,
    loadCarbonBankSubmissions,
    loadCarbonBankOverview,
  } = useCarbonDashboardStore();
  const { t } = useTran();
  const [selectedYear, setSelectedYear] = useState("all");
  const [selectedOrgId, setSelectedOrgId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    reviewStatus: "all",
    consentStatus: "all",
    usableOnly: false,
  });

  const yearOptions = useMemo(() => generateYearOptions(), []);
  const hasTargets = true;
  const { user } = useAuthStore();
  const permissions = user?.permissions || [];
  const loginOrgId =
    user?.organizationUnitId ||
    user?.organizationUnit?.id ||
    user?.organization?.id ||
    null;
  const role = user?.role || null;
  const navigate = useNavigate();
  const { fetchHierarchy, tree, loading: orgLoading } = useOrganizationStore();

  useEffect(() => {
    loadCarbonTargets();
    loadCarbonBankSubmissions(selectedYear);
    loadCarbonBankOverview(selectedYear);
  }, [selectedYear]);

  useEffect(() => {
    if (role === "sme_user") {
      const firstOrg = user?.organizationUnitId?.[0];
      if (firstOrg?.id) {
        setSelectedOrgId(firstOrg.id);
      } else if (user?.assignedToParentCompany && user?.companyId) {
        setSelectedOrgId(user.companyId);
      }
    }
  }, []);

  const smeOrg = user?.organizationUnitId?.[0];
  const organizations =
    role === "sme_user"
      ? [
          smeOrg
            ? { name: smeOrg.name, id: smeOrg.id }
            : user?.assignedToParentCompany
              ? {
                  name: user?.companyName || "Parent Company",
                  id: user?.companyId,
                }
              : { name: "", id: "" },
        ]
      : tree;

  useEffect(() => {
    if (!orgLoading && !tree.length && role !== "sme_user") {
      fetchHierarchy({ selectId: loginOrgId }).catch(() => {});
    }
  }, [fetchHierarchy, loginOrgId, tree.length]);

  const targets = useMemo(() => {
    if (selectedYear === "all") {
      const aggregatedTargets = {
        year: "all",
        scope1: {
          total: 0,
          stationaryCombustion: 0,
          mobileCombustion: 0,
          fugitiveEmissions: 0,
          processEmissions: 0,
        },
        scope2: {
          total: 0,
          purchasedElectricity: 0,
          purchasedSteam: 0,
          purchasedCooling: 0,
        },
      };

      let hasAnyTargets = false;
      if (carbonTargets?.targets) {
        carbonTargets?.targets.forEach((targets) => {
          if (targets) {
            hasAnyTargets = true;
            if (targets.scope1) {
              aggregatedTargets.scope1.total += targets.scope1.total || 0;
              aggregatedTargets.scope1.stationaryCombustion +=
                targets.scope1.stationaryCombustion || 0;
              aggregatedTargets.scope1.mobileCombustion +=
                targets.scope1.mobileCombustion || 0;
              aggregatedTargets.scope1.fugitiveEmissions +=
                targets.scope1.fugitiveEmissions || 0;
              aggregatedTargets.scope1.processEmissions +=
                targets.scope1.processEmissions || 0;
            }
            if (targets.scope2) {
              aggregatedTargets.scope2.total += targets.scope2.total || 0;
              aggregatedTargets.scope2.purchasedElectricity +=
                targets.scope2.purchasedElectricity || 0;
              aggregatedTargets.scope2.purchasedSteam +=
                targets.scope2.purchasedSteam || 0;
              aggregatedTargets.scope2.purchasedCooling +=
                targets.scope2.purchasedCooling || 0;
            }
          }
        });
      }
      return aggregatedTargets;
    } else {
      const targetsData = carbonTargets;
      if (Array.isArray(targetsData?.targets)) {
        const yearTarget = targetsData.targets.find(
          (t) => t.year === selectedYear,
        );
        return yearTarget;
      } else if (targetsData?.year === selectedYear) {
        return targetsData;
      } else {
        return null;
      }
    }
  }, [selectedYear, carbonTargets]);

  const submissionCounts = useMemo(() => {
    return carbonSubmissionsData.reduce((acc, sub) => {
      const orgId = sub.organizationUnit?.id;
      if (orgId) acc[orgId] = (acc[orgId] || 0) + 1;
      return acc;
    }, {});
  }, [carbonSubmissionsData]);

  const performance = useMemo(() => {
    if (!carbonOverviewData || !carbonSubmissionsData.length) return null;
    let filteredSubmissions = selectedOrgId
      ? carbonSubmissionsData.filter(
          (s) => s.organizationUnit?.id === selectedOrgId,
        )
      : carbonSubmissionsData;

    if (filters.reviewStatus !== "all") {
      filteredSubmissions = filteredSubmissions.filter(
        (s) => s.bankReview?.status === filters.reviewStatus,
      );
    }

    if (filters.consentStatus !== "all") {
      filteredSubmissions = filteredSubmissions.filter(
        (s) => s.consent?.status === filters.consentStatus,
      );
    }

    if (filters.usableOnly) {
      filteredSubmissions = filteredSubmissions.filter(
        (s) =>
          s.status === "submitted" &&
          s.bankReview?.status === "approved" &&
          s.consent?.status === "approved",
      );
    }

    if (filteredSubmissions.length === 0) return null;
    let filteredOverview = carbonOverviewData;
    if (selectedOrgId) {
      const aggregated = filteredSubmissions.reduce(
        (acc, sub) => {
          const isUsable =
            sub.status === "submitted" && sub.bankReview?.status === "approved";
          //  && sub.consent?.status === 'approved';

          if (isUsable) {
            acc.scope1TotalTco2e += sub.totals?.scope1TotalTco2e || 0;
            acc.scope2TotalTco2e += sub.totals?.scope2TotalTco2e || 0;
          }
          return acc;
        },
        { scope1TotalTco2e: 0, scope2TotalTco2e: 0 },
      );
      filteredOverview = { aggregated, reportingYear: selectedYear };
    }
    return calculateCarbonPerformance(
      targets,
      filteredOverview,
      filteredSubmissions,
    );
  }, [
    targets,
    carbonOverviewData,
    carbonSubmissionsData,
    selectedOrgId,
    selectedYear,
    filters,
  ]);

  return (
    <main className="p-6">
      <div className="mx-auto max-w-[1600px] space-y-4">
        <div className="flex flex-col gap-4">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 dark:text-white">
                {t("Carbon Inventory Dashboard")}
              </h1>
              <p className="text-sm text-slate-500 dark:text-slate-400">
                {t(
                  "Monitor SME emissions against targets and track performance",
                )}
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Can
                resource="carbon_target_setting"
                action="view"
                userPermissions={permissions}
              >
                <Button
                  variant="outline"
                  onClick={() => navigate(PATHS.CARBON_TARGETS)}
                  className="gap-2"
                >
                  <Settings className="h-4 w-4" />
                  {t("Set Targets")}
                </Button>
              </Can>
              <Can
                resource="carbon_surveys"
                action="view"
                userPermissions={permissions}
              >
                <Button
                  variant="outline"
                  onClick={() => navigate(PATHS.CARBON_SURVEYS)}
                  className="gap-2"
                >
                  <FileText className="h-4 w-4" />
                  {t("Carbon Inventory Surveys")}
                </Button>
              </Can>
              <Can
                resource="carbon_submissions"
                action="view"
                userPermissions={permissions}
              >
                <Button
                  variant="outline"
                  onClick={() => navigate(PATHS.CARBON_SUBMISSIONS)}
                  className="gap-2"
                >
                  <FileText className="h-4 w-4" />
                  {t("View Submissions")}
                </Button>
              </Can>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <CarbonDashboardFilters
            filters={filters}
            onFiltersChange={setFilters}
            organizations={organizations}
            selectedOrgId={selectedOrgId}
            onOrgChange={setSelectedOrgId}
            submissionCounts={submissionCounts}
            selectedYear={selectedYear}
            onYearChange={setSelectedYear}
            yearOptions={yearOptions}
            role={role}
          />
          {loading ? (
            <div className="text-center py-12">
              <RefreshCw className="h-8 w-8 animate-spin mx-auto text-emerald-600" />
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                Loading dashboard...
              </p>
            </div>
          ) : !hasTargets ? (
            <Card className="p-12 text-center border-slate-200 dark:border-slate-800">
              <Settings className="h-16 w-16 mx-auto text-slate-400" />
              <h3 className="mt-4 text-lg font-medium text-slate-900 dark:text-white">
                {selectedYear === "all"
                  ? "No Targets Set"
                  : `No Targets Set for ${selectedYear}`}
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {t("Set carbon inventory targets to view dashboard analytics")}
              </p>
              <Button
                className="mt-4 bg-emerald-600 hover:bg-emerald-700"
                onClick={() => navigate(RoutePaths.ESG_KPI_TARGETS)}
              >
                {t("Go to Target Settings")}
              </Button>
            </Card>
          ) : !performance ? (
            <Card className="p-12 text-center border-slate-200 dark:border-slate-800">
              <FileText className="h-16 w-16 mx-auto text-slate-400" />
              <h3 className="mt-4 text-lg font-medium text-slate-900 dark:text-white">
                {t("No Submission Data")}
              </h3>
              <p className="mt-2 text-sm text-slate-500 dark:text-slate-400">
                {selectedOrgId
                  ? t("This organization unit has no submissions yet")
                  : t("SME submissions will appear here once submitted")}
              </p>
            </Card>
          ) : (
            <>
              <CarbonOverviewCards
                performance={performance}
                year={selectedYear}
              />
              {selectedYear !== "all" && (
                <CarbonTrendAnalysis currentYear={selectedYear} />
              )}
              <CarbonPerformanceCharts performance={performance} />
              <CarbonActivityBreakdown
                targets={targets}
                performance={performance}
              />
              <CarbonSMEContribution
                contributions={performance.smeContributions}
                year={selectedYear}
              />
            </>
          )}
        </div>
      </div>
    </main>
  );
});

export default CarbonInventoryDashboardPage;
